import {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useMemo,
  ReactNode,
} from 'react';
import Cookies from 'universal-cookie';
import posthog from 'posthog-js';
import { User } from '../services/usersService';

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
}

type AuthAction =
  | { type: 'LOGIN'; payload: { user: User; token: string } }
  | { type: 'LOGOUT' };

interface AuthContextType extends AuthState {
  login: (user: User, token: string) => void;
  logout: () => void;
}

// Reducer function
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        token: action.payload.token,
      };
    case 'LOGOUT':
      return {
        isAuthenticated: false,
        user: null,
        token: null,
      };
    default:
      return state;
  }
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const loadInitialState = (): AuthState => {
  const cookies = new Cookies();
  const token = cookies.get('accessToken');
  const userJson = localStorage.getItem('user');
  const user = userJson ? JSON.parse(userJson) : null;

  return {
    isAuthenticated: Boolean(token),
    user,
    token,
  };
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  // Load initial state from localStorage
  const [state, dispatch] = useReducer(authReducer, loadInitialState());

  // Identify returning visitors whose session persisted across page reloads.
  // Fresh logins are identified in LoginForm; this covers the case where the
  // user reloads without going through the login flow again.
  const initialUser = useRef(state.user);
  useEffect(() => {
    if (initialUser.current) {
      const user = initialUser.current;
      posthog.identify(String(user.id), {
        email: user.email,
        name: `${user.firstName} ${user.lastName}`,
      });
    }
  }, []);

  const login = (user: User, token: string) => {
    const cookies = new Cookies();
    cookies.set('accessToken', token, {
      path: '/',
      domain: import.meta.env.VITE_APP_DOMAIN,
    });
    localStorage.setItem('user', JSON.stringify(user));
    dispatch({
      type: 'LOGIN',
      payload: { user, token },
    });
  };

  const logout = () => {
    const cookies = new Cookies();
    cookies.remove('accessToken', {
      path: '/',
      domain: import.meta.env.VITE_APP_DOMAIN,
    });
    localStorage.removeItem('user');
    dispatch({ type: 'LOGOUT' });
  };

  const value = useMemo(
    () => ({
      ...state,
      login,
      logout,
    }),
    [state]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
